import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import { formatURL, redirectToUrl } from '../../../services/AppUtils';
import AppConfiguration from '../../../services/AppConfiguration';

/**
 * This component shows the result list.
 * @extends {Component}
 */
class ResultListHits extends React.Component {
  constructor(props) {
    super(props);

    /**
     * This is a constant used for the font rendered image url size param. It defines the padding percentage
     * the image has in the url. We must add this to the image size param in order to get the needed height.
     * @type {Number}
     */
    this.imagePaddingPercentage = 0.22;

    /**
     * Instance of the App configuration data.
     * @type {Object}
     */
    this.appConfig = AppConfiguration.getConfig();

    /**
     * Component initial state.
     * @type {Object}
     * @property {Array} list       List of fonts data. It uses a deep clone in order to avoid prop mutation.
     * @property {Number} pageShown The number of the page shown on the results list
     */
    this.state = {
      list: cloneDeep(props.predictedList),
      // The minimum value for startOnPage should be 1. If the value is less than 1, we need to force it to 1.
      pageShown: props.startOnPage >= 1 ? props.startOnPage : 1,
    };

    /**
     * Configure all result links to open on a new tab.
     */
    this.linksTarget = '_blank';

    /**
     * Bind methods.
     */
    this.addMoreResults = this.addMoreResults.bind(this);
  }

  /**
   * If the fontPreviewText, size or list has changed, update the list state with the unmutated list prop.
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    if (
      this.props.fontPreviewText !== prevProps.fontPreviewText ||
      this.props.imageSize !== prevProps.imageSize ||
      !this.areListPropsEquals(prevProps.predictedList, this.props.predictedList)
    ) {
      this.updateStateOnTextImageSizeChange();
    }
  }

  /**
   * After an error occurs loading the image, it assigns a new object attribute `noSampleImage` to let the
   * component know that it must render a default image.
   * @param {Number} index image position in the list.
   */
  onImageError(index) {
    const listState = cloneDeep(this.state.list);
    listState[index].noSampleImage = this.appConfig.fontRenderNoSampleAvailable;

    this.setState({
      list: listState,
    });
  }

  /**
   * It builds the image list source url with the provided data.
   * @param {String} MD5Hash The font style MD5Hash.
   * @returns {String}
   */
  getImageSource(MD5Hash) {
    const wtfSIGEndpoint = this.appConfig.SIGRenderApi.endpoints.render;
    const wtfSIGUrl = this.appConfig.SIGRenderApi.url;
    const parsedImageSize = parseInt(this.props.imageSize, 10);
    const fontSize = parsedImageSize + Math.round((parsedImageSize * this.imagePaddingPercentage));
    let { fontPreviewText } = this.props;
    const { predictionText } = this.props;

    // Encode the fontPreviewText to allow special characters like # and &.
    fontPreviewText = encodeURIComponent(fontPreviewText);

    // If the `fontPreviewText` is defined, `render` endpoint is built with the `text` param.
    const textData = fontPreviewText ? { rt: fontPreviewText } : { rt: predictionText };
    const params = {
      MD5Hash,
      fontSize,
    };
    return `${wtfSIGUrl}${formatURL(wtfSIGEndpoint, { ...textData, ...params })}`;
  }

  /**
   * It performs a deep comparison between the previous list prop and the current one.
   * @param {Array} prevList    The previous component list prop.
   * @param {Array} currentList The current component list prop.
   * @returns {Boolean}
   */
  areListPropsEquals(prevList, currentList) {
    return JSON.stringify(prevList) === JSON.stringify(currentList);
  }

  /**
   * It updates the `list` state with the predictedList prop
   * since it is not mutated. We assign the initial value unchanged.
   */
  updateStateOnTextImageSizeChange() {
    this.setState({
      list: cloneDeep(this.props.predictedList),
    });
  }

  /**
   * Add one more page of results to the list.
   * This method also calls the onPageChange callback to notify about the change.
   */
  addMoreResults() {
    const newPage = this.state.pageShown + 1;
    this.setState({
      pageShown: newPage,
    });

    // Expose the new page shown
    this.props.onPageChange(newPage);
  }

  /**
   * Render the list image element
   * @param {Object} MD5Hash             The font MD5Hash hash.
   * @param {Number} index            Index for the iterated item.
   * @param {Object} imageStyleHeight The height style of the image.
   * @returns {ReactElement} The image react element
   */
  renderImage(MD5Hash, index, imageStyleHeight) {
    const imgSrc = this.getImageSource(MD5Hash);
    let dataToRender = null;
    // SIG itself give us no sample data image if there is no image for given MD5Hash
    // if (data.noSampleImage) {
    //   dataToRender = (
    //     <div>
    //       <img
    //         src={data.noSampleImage}
    //         alt='Font render no sample available'
    //       />
    //     </div>
    //   );
    dataToRender = (
      <img
        src={imgSrc}
        style={imageStyleHeight}
        alt='Font Render'
        onError={() => this.onImageError(index)}
      />
    );
    // }

    return dataToRender;
  }

  /**
   * Render list item for crop result
   * @param {Object} data             The font data object.
   * @param {Number} index            Index for the iterated item.
   * @param {Object} imageStyleHeight The height style of the image.
   * @return {ReactElement}
   */
  renderListItem(data, index, imageStyleHeight) {
    let price;
    const productUrl = `/products/${data.handle}`;
    const currencyType = window.Shopify.currency.active;
    if (data.prices && data.prices[currencyType]) {
      price = window.jsCurrency.format(data.prices[currencyType]);
    } else {
      // Grab the few global variable expose by the Shopify MyFonts to calculate price with currency type
      price = window.jsCurrency.format(data.price * window.conversionRate);
    }
    // Grab the few global variable expose by the Shopify MyFonts to calculate price with currency type
    if (window.changeLimiters) {
      price = window.changeLimiters(
        price,
        currencyType
      );
    }
    return (
      <div key={index} className='wtfResultList_row'>
        <div className='wtfResultList_textsContainer'>
          <div className='wtfResultList_title'>
            <a
              className='wtfResultList_titleFontLink'
              href={productUrl}
              target={this.linksTarget}
              id={`wtfResultList_titleFontLink_${index}`}
            >
              {data.name}
            </a>
            <span className='wtfResultList_titleBy'>by</span>
            <span className='wtfResultList_titleFoundry'>{data.foundry_name}</span>
          </div>
          <div
            className='wtfResultList_fontRender'
            id={`wtfResultList_fontRender_${index}`}
            onClick={() => redirectToUrl(productUrl, this.linksTarget)}
            onKeyPress={() => redirectToUrl(productUrl, this.linksTarget)}
            role='button'
            tabIndex='0'
          >
            {this.renderImage(data.default_variation_MD5, index, imageStyleHeight)}
          </div>
        </div>
        <div className='wtfResultList_priceInfoContainer'>
          <span className='wtfResultList_price'>
            {price}
          </span>
          <button
            id={`wtfResultList_button_${index}`}
            className='wtfResultList_button'
            onClick={() => redirectToUrl(productUrl, this.linksTarget)}
          >
            Get it
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render the list of results
   *
   * @returns {ReactElement} The list of results
   */
  renderList() {
    const imageStyleHeight = {
      height: `${this.props.imageHeight}px`,
    };

    // Calculate the last item to show. Pagination is taken into account.
    const lastItemShown = this.state.pageShown * this.props.resultsPerPage;
    // If pagination is on, show a subset of the list provided
    const resultsToRender = this.state.list.slice(0, lastItemShown);
    return resultsToRender.map((data, index) => this.renderListItem(data, index, imageStyleHeight));
  }

  /**
   * Render the show more button. If the list is completely shown, then the button is not rendered.
   *
   * @returns {ReactElement} The show more button element.
   */
  renderShowMoreButton() {
    // Do not render the button if the predictedStyles is completely shown.
    if (this.state.pageShown * this.props.resultsPerPage >= this.state.list.length) {
      return null;
    }
    return (
      <button
        onClick={this.addMoreResults}
        className='wtfResultList_button wtfResultList_showMoreButton'
      >
        Show more results
      </button>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderList()}
        {this.renderShowMoreButton()}
      </React.Fragment>
    );
  }
}

/**
 * Set the ResultList component props default values.
 * @type {Object}
 * @property {Array}  styleList         list of fonts data.
 * @property {String} fontPreviewText   font preview text value that the image will render. If `predictionText` prop
 *                                      is provided, this is ignored.
 * @property {String} predictionText    prediction encrypted text key that the image will render.
 * @property {String} imageSize         This is the size the image will render.
 * @property {String} imageHeight       It will change the height of the image already rendered in order to create a
 * @property {Number} resultsPerPage    The number of results shown per page when using pagination.
 * @property {Number} startOnPage       The number of the page that should be shown at startup.
 * @property {Function} onPageChange    Callback to expose the number of page shown on the Result List
 */
ResultListHits.defaultProps = {
  predictedList: [],
  fontPreviewText: '',
  predictionText: '',
  imageSize: '',
  imageHeight: '',
  resultsPerPage: 20,
  startOnPage: 1,
  onPageChange: () => {},
};

/**
 * Declare the ResultList component PropTypes for better validation.
 * @type {Object}
 */
ResultListHits.propTypes = {
  predictedList: PropTypes.arrayOf(PropTypes.shape({
    default_variation_MD5: PropTypes.string,
    foundry_title: PropTypes.string,
    handle: PropTypes.string,
    id: PropTypes.string,
    myfonts_style_id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.string,
    url: PropTypes.string,
  })),
  fontPreviewText: PropTypes.string,
  predictionText: PropTypes.string,
  imageSize: PropTypes.string,
  imageHeight: PropTypes.string,
  resultsPerPage: PropTypes.number,
  startOnPage: PropTypes.number,
  onPageChange: PropTypes.func,
};
export default ResultListHits;



// WEBPACK FOOTER //
// ./src/components/Result/ResultListHits/ResultListHits.js